/**
 * CardTag - muted metadata tag for content cards.
 *
 * The quiet, never-louder-than-the-title treatment shared by card badges
 * across Plans, Workouts, and Exercises (difficulty, plan chip, Public).
 * Two variants, matching the two species that existed before extraction:
 * - `filled` (default): field-tone fill, muted uppercase label — for
 *   enum-ish metadata (difficulty, visibility).
 * - `outline`: hairline stroke, sentence case — for referential chips that
 *   carry an entity name (e.g. the workout card's plan chip).
 *
 * Not a `Badge`: Badge variants are prominent semantic-colour fills; CardTag
 * is deliberately whisper-grade chrome on `contrast-700` cards.
 */

import type { CardTagProps, CardTagVariant } from './CardTag.types';

const VARIANT_CLASSES: Record<CardTagVariant, string> = {
  filled: 'bg-contrast-600 text-neutral-400',
  outline: 'border border-stroke-default text-neutral-500',
};

/** Uppercase suits enum-ish metadata, not entity names or sentence case. */
const VARIANT_CAPS: Record<CardTagVariant, boolean> = {
  filled: true,
  outline: false,
};

export function CardTag({
  variant = 'filled',
  icon,
  caps,
  children,
  className = '',
  ...rest
}: CardTagProps): React.JSX.Element {
  const isCaps = caps ?? VARIANT_CAPS[variant];

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-[3px] text-label-xs font-medium ${
        isCaps ? 'uppercase' : ''
      } ${VARIANT_CLASSES[variant]} ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
      {...rest}
    >
      {icon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

export default CardTag;
